import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import {
  CheckCircle2,
  AlertCircle,
  Info,
  GripVertical,
  Plus,
  Trash2,
  Sparkles
} from 'lucide-react';

interface Question {
  id: string;
  text: string;
  type?: string;
  category?: string;
  reasoning?: string;
  priority?: string;
  included?: boolean;
  source?: string;
  metrics?: any;
}

interface QuestionCuratorProps {
  questions: Question[];
  selectedQuestions: Question[];
  onChange: (questions: Question[]) => void;
}

export function QuestionCurator({ questions, selectedQuestions, onChange }: QuestionCuratorProps) {
  const [localQuestions, setLocalQuestions] = useState<Question[]>([]);
  const [customQuestion, setCustomQuestion] = useState('');

  useEffect(() => {
    if (questions.length > 0) {
      // Merge with selected questions state
      const merged = questions.map((q) => {
        const existing = selectedQuestions.find((sq) => sq.id === q.id);
        // Handle both 'included' and 'selected' properties (wheel questions use 'selected')
        const isIncluded = existing?.included ?? existing?.selected ?? q.included ?? (q as any).selected ?? false;
        return { ...q, included: isIncluded };
      });
      setLocalQuestions(merged);
    } else if (selectedQuestions.length > 0) {
      // Handle 'selected' property from wheel questions
      const normalized = selectedQuestions.map(q => ({
        ...q,
        included: q.included ?? (q as any).selected ?? true
      }));
      setLocalQuestions(normalized);
    }
  }, [questions, selectedQuestions]);

  const handleToggle = (questionId: string) => {
    const updated = localQuestions.map((q) =>
      q.id === questionId ? { ...q, included: !q.included } : q
    );
    setLocalQuestions(updated);
    onChange(updated.filter((q) => q.included));
  };

  const handleAddCustom = () => {
    if (!customQuestion.trim()) return;

    const newQuestion: Question = {
      id: `custom_${Date.now()}`,
      text: customQuestion.trim(),
      type: 'custom',
      included: true,
      priority: 'medium'
    };

    const updated = [...localQuestions, newQuestion];
    setLocalQuestions(updated);
    onChange(updated.filter((q) => q.included));
    setCustomQuestion('');
  };

  const handleRemove = (questionId: string) => {
    const updated = localQuestions.filter((q) => q.id !== questionId);
    setLocalQuestions(updated);
    onChange(updated.filter((q) => q.included));
  };

  const handleSelectAll = () => {
    const updated = localQuestions.map((q) => ({ ...q, included: true }));
    setLocalQuestions(updated);
    onChange(updated);
  };

  const handleDeselectAll = () => {
    const updated = localQuestions.map((q) => ({ ...q, included: false }));
    setLocalQuestions(updated);
    onChange([]);
  };

  const selectedCount = localQuestions.filter((q) => q.included).length;
  const typeColors: Record<string, string> = {
    opening: 'bg-blue-500',
    core: 'bg-green-500',
    closing: 'bg-purple-500',
    follow_up: 'bg-orange-500',
    custom: 'bg-pink-500'
  };

  const priorityColors: Record<string, string> = {
    high: 'bg-red-500',
    medium: 'bg-yellow-500',
    low: 'bg-gray-500'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Review & Select Questions</h3>
          <p className="text-sm text-muted-foreground">
            {selectedCount} of {localQuestions.length} questions selected
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleSelectAll}>
            Select All
          </Button>
          <Button variant="outline" size="sm" onClick={handleDeselectAll}>
            Deselect All
          </Button>
        </div>
      </div>

      {/* Info Alert */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          {localQuestions.some(q => q.source === 'question_wheel') ? (
            <>Review the questions from your question wheel. These are sorted by opportunity score and expert-suitability. You can also add custom questions below.</>
          ) : (
            <>Review the AI-generated questions and select which ones to include in the interview. You can also add your own custom questions below.</>
          )}
        </AlertDescription>
      </Alert>

      {/* Questions List */}
      {localQuestions.length === 0 ? (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No questions have been generated yet. Please complete the previous steps.
          </AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-3">
          {localQuestions.map((question, index) => (
            <Card
              key={question.id}
              className={`transition-all ${
                question.included ? 'ring-2 ring-primary' : ''
              }`}
            >
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  {/* Checkbox */}
                  <Checkbox
                    id={question.id}
                    checked={question.included}
                    onCheckedChange={() => handleToggle(question.id)}
                    className="mt-1"
                  />

                  {/* Question Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <label
                        htmlFor={question.id}
                        className="font-medium cursor-pointer flex-1"
                      >
                        {index + 1}. {question.text}
                      </label>

                      {/* Delete button for custom questions */}
                      {question.type === 'custom' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemove(question.id)}
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>

                    {/* Badges */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Type badge (AI-generated questions) */}
                      {question.type && (
                        <Badge
                          className={`${typeColors[question.type] || 'bg-gray-500'} text-white text-xs`}
                        >
                          {question.type.replace('_', ' ')}
                        </Badge>
                      )}

                      {/* Category badge (wheel questions) */}
                      {!question.type && question.category && (
                        <Badge
                          className={`${typeColors[question.category] || 'bg-indigo-500'} text-white text-xs`}
                        >
                          {question.category.replace('_', ' ')}
                        </Badge>
                      )}

                      {/* Source badge */}
                      {question.source === 'question_wheel' && (
                        <Badge variant="outline" className="text-xs bg-purple-50 border-purple-300 text-purple-700">
                          From Wheel
                        </Badge>
                      )}

                      {/* Priority badge */}
                      {question.priority && (
                        <Badge
                          className={`${priorityColors[question.priority]} text-white text-xs`}
                        >
                          {question.priority}
                        </Badge>
                      )}

                      {/* Opportunity score for wheel questions */}
                      {question.metrics?.opportunityScore > 0 && (
                        <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
                          Opp: {question.metrics.opportunityScore}
                        </Badge>
                      )}

                      {/* Selected indicator */}
                      {question.included && (
                        <Badge variant="outline" className="text-xs border-primary text-primary">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Selected
                        </Badge>
                      )}
                    </div>

                    {/* Reasoning */}
                    {question.reasoning && (
                      <div className="mt-2 text-xs text-muted-foreground bg-muted p-2 rounded">
                        <strong>AI Reasoning:</strong> {question.reasoning}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Custom Question */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Plus className="w-4 h-4" />
            Add Custom Question
          </CardTitle>
          <CardDescription>
            Have a specific question in mind? Add it here
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            placeholder="Enter your custom question..."
            value={customQuestion}
            onChange={(e) => setCustomQuestion(e.target.value)}
            rows={3}
          />
          <Button onClick={handleAddCustom} disabled={!customQuestion.trim()}>
            <Plus className="w-4 h-4 mr-2" />
            Add Question
          </Button>
        </CardContent>
      </Card>

      {/* Summary */}
      {selectedCount > 0 && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            <strong>{selectedCount} questions</strong> will be included in the interview briefing.
            Estimated duration: <strong>{Math.round(selectedCount * 2.5)} minutes</strong>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
